import { getCreateSiteUrl, getUpdateSiteUrl } from 'generatedTypes/client/services/site-controller';
import { getAssignSiteToBoundaryUrl } from 'generatedTypes/flight/services/boundary-controller';
import { useState } from 'react';
import { isDemoHiddenName } from 'utils/demoRecordFilter';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { SITES_API } from './constants';
import type {
    AssignFieldToBoundary,
    CreateField,
    FetchField,
    FetchFieldHierarchyFormItems,
    SiteResponse,
    SiteWithParcelAndClientResponse,
    UpdateField
} from './types';

const useField = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchField: FetchField = async ({ id }) => {
        try {
            setIsLoading(true);

            const response = await withRetry(
                () => authenticatedGet<SiteWithParcelAndClientResponse>(`${SITES_API}/${id}`),
                CONNECTION_ERROR,
                () => fetchField({ id })
            );

            if (response && isDemoHiddenName(response.name)) {
                return null;
            }

            return response;
        } finally {
            setIsLoading(false);
        }
    };

    const createField: CreateField = async ({ farmId, field }) => {
        try {
            setIsLoading(true);
            const url = getCreateSiteUrl(farmId);

            return await withRetry(
                () => authenticatedPost<SiteResponse>(url, JSON.stringify(field)),
                SAVE_FAILED,
                () => createField({ farmId, field })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateField: UpdateField = async ({ field, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateSiteUrl(id);

            return await withRetry(
                () => authenticatedPut<SiteResponse>(url, JSON.stringify(field)),
                SAVE_FAILED,
                () => updateField({ field, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const assignFieldToBoundary: AssignFieldToBoundary = async ({ boundaryId, siteId }) => {
        try {
            setIsLoading(true);
            const url = getAssignSiteToBoundaryUrl(boundaryId, siteId);

            return await withRetry(
                () => authenticatedPut(url, null),
                SAVE_FAILED,
                () => assignFieldToBoundary({ boundaryId, siteId })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFieldHierarchyFormItems: FetchFieldHierarchyFormItems = async ({ id }) => {
        const fieldData = await fetchField({ id });

        if (!fieldData) {
            return null;
        }

        return {
            client: {
                label: fieldData.clientName ?? '',
                value: fieldData.clientId ?? ''
            },
            farm: {
                label: fieldData.parcelName ?? '',
                value: fieldData.parcelId ?? ''
            },
            field: {
                label: fieldData.name ?? '',
                value: fieldData.id ?? ''
            }
        };
    };

    return {
        assignFieldToBoundary,
        createField,
        fetchField,
        fetchFieldHierarchyFormItems,
        isLoading,
        updateField
    };
};

export default useField;
